import React, { useState, useEffect, useRef } from "react";
import "./RallyRoadOverlay.css";

const RallyRoadOverlay = ({ scrollProgress = 0 }) => {
  const pathRef = useRef(null);
  const svgRef = useRef(null);

  // SVG path adjusted for 1500 width viewBox
  const rallyPath =
    "M0 158.499Q30 158 62 158.499L372.03 125.757C382.853 124.798 393.426 122.792 403.448 119.793L548.589 76.346C566.499 70.9871 585.73 68.0178 605.184 67.6137L695.071 65.7339C702.373 65.5764 709.644 64.8235 716.706 63.4703L743.858 58.289C768.308 53.6198 794.071 53.5012 818.402 57.9342L1246 135.994C1270.14 140.404 1295.61 140.739 1320.2 136.979L1488.18 108.192Q1500 107 1500 110";

  // Calculate the marker position along the path
  const getMarkerPosition = (progress) => {
    if (!pathRef.current) {
      // Fallback calculation if SVG path is not available
      const x = 0 + progress * 1500; // Full width from 0 to 1500
      const y = 158.499 - progress * 50 + Math.sin(progress * Math.PI * 3) * 20; // Approximate the curve
      return { x, y };
    }

    try {
      // Use SVG path methods for accurate positioning
      const pathElement = pathRef.current;
      const pathLength = pathElement.getTotalLength();
      const point = pathElement.getPointAtLength(progress * pathLength);
      return { x: point.x, y: point.y };
    } catch (error) {
      // Fallback if SVG methods fail
      const x = 0 + progress * 1500; // Full width from 0 to 1500
      const y = 158.499 - progress * 50 + Math.sin(progress * Math.PI * 3) * 20;
      return { x, y };
    }
  };

  const markerPos = getMarkerPosition(scrollProgress);

  // Calculate responsive marker radius
  const getMarkerRadius = () => {
    const isMobile = window.innerWidth <= 768;
    return isMobile ? 36 : 18;
  };

  const [markerRadius, setMarkerRadius] = React.useState(getMarkerRadius());

  // Calculate uniform scale factor to make SVG width match viewport width
  const [uniformScale, setUniformScale] = React.useState(1);

  React.useEffect(() => {
    const updateResponsiveValues = () => {
      const viewportWidth = window.innerWidth;
      const svgNaturalWidth = 1500; // viewBox width
      const scale = viewportWidth / svgNaturalWidth;
      setUniformScale(scale);

      // Update marker radius based on screen size
      setMarkerRadius(getMarkerRadius());
    };

    updateResponsiveValues();
    window.addEventListener("resize", updateResponsiveValues);
    window.addEventListener("orientationchange", updateResponsiveValues);

    return () => {
      window.removeEventListener("resize", updateResponsiveValues);
      window.removeEventListener("orientationchange", updateResponsiveValues);
    };
  }, []);

  return (
    <div className="rally-road-overlay" style={{ height: 200 * uniformScale }}>
      <svg
        ref={svgRef}
        className="rally-road-svg"
        width={1500 * uniformScale}
        height={200 * uniformScale}
        viewBox="0 0 1500 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        style={{ pointerEvents: "none" }}
      >
        {/* Rally road path */}
        <path
          ref={pathRef}
          d={rallyPath}
          stroke="var(--color-highlight-yellow, #FFD700)"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          className="rally-path"
        />

        {/* Expanding wave rings - responsive radius */}
        <circle
          cx={markerPos.x}
          cy={markerPos.y}
          r={markerRadius}
          fill="none"
          stroke="var(--color-highlight-yellow, #FFD700)"
          strokeWidth="2"
          opacity="0.6"
          className="rally-wave rally-wave-1"
        />
        <circle
          cx={markerPos.x}
          cy={markerPos.y}
          r={markerRadius}
          fill="none"
          stroke="var(--color-highlight-yellow, #FFD700)"
          strokeWidth="1"
          opacity="0.4"
          className="rally-wave rally-wave-2"
        />

        {/* Main stable dot - responsive radius */}
        <circle
          cx={markerPos.x}
          cy={markerPos.y}
          r={markerRadius}
          fill="var(--color-highlight-yellow, #FFD700)"
          stroke="rgba(255, 255, 255, 0.8)"
          strokeWidth="2"
          className="rally-marker"
        />
      </svg>
    </div>
  );
};

export default RallyRoadOverlay;
